import { IconChevronDown } from '@rossum/ui/icons/tabler';
import { Chip, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import * as R from 'remeda';
import styles from './style.module.sass';

type Props = {
  activeChildrenLength?: number | null;
  children: ReactNode;
  childrenLength: number;
  controlElements?: ReactNode;
  defaultExpand?: boolean;
  disabled?: boolean;
  forceExpand?: boolean;
  name: string;
  nameComponent?: (params: NameComponentsProps) => ReactNode;
  forceControlElements?: boolean;
  headerClassName?: string;
};

type NameComponentsProps = {
  name: string;
  disabled: boolean;
  badgeComponent: ReactNode;
};

const Collapse = ({
  activeChildrenLength = null,
  children,
  childrenLength,
  controlElements = null,
  defaultExpand = false,
  disabled = false,
  forceExpand = false,
  name,
  nameComponent = ({
    name: nameToRender,
    badgeComponent,
  }: NameComponentsProps) => (
    <Stack direction="row" spacing={1} sx={{ pl: 1 }} alignItems="center">
      <Typography
        variant="body2"
        color={disabled ? 'text.disabled' : 'text.primary'}
      >
        {nameToRender}
      </Typography>
      {badgeComponent}
    </Stack>
  ),
  forceControlElements = false,
  headerClassName,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpand);

  useEffect(() => {
    if (!expanded && forceExpand) setExpanded(true);
  }, [expanded, forceExpand]);

  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <div className={styles.Wrapper}>
      <div className={clsx(styles.Header, headerClassName)}>
        <div
          className={clsx(styles.Label, !childrenLength && styles.Empty)}
          onClick={childrenLength ? toggleExpanded : undefined}
        >
          <div className={styles.IconWrapper}>
            <SvgIcon
              color="primary"
              sx={{
                fontSize: 16,
                transform: `rotate(${expanded ? 0 : -90}deg)`,
              }}
            >
              <IconChevronDown />
            </SvgIcon>
          </div>
          {nameComponent &&
            nameComponent({
              name,
              disabled,
              badgeComponent: (
                <Chip
                  color={
                    activeChildrenLength === null
                      ? childrenLength === 0
                        ? 'default'
                        : 'info'
                      : activeChildrenLength === 0
                        ? 'default'
                        : 'info'
                  }
                  label={
                    R.isNumber(activeChildrenLength)
                      ? `${activeChildrenLength} / ${childrenLength}`
                      : `${childrenLength}`
                  }
                />
              ),
            })}
        </div>
        {!!childrenLength && (
          <div
            className={clsx(
              styles.ActionButtons,
              (expanded || forceControlElements) && styles.ActionButtonsExpanded
            )}
          >
            {controlElements}
          </div>
        )}
      </div>
      <CSSTransition
        in={expanded}
        timeout={400}
        appear
        mountOnEnter
        unmountOnExit
        classNames={{
          enter: styles.Enter,
          enterActive: styles.EnterActive,
          exitActive: styles.ExitActive,
        }}
      >
        {children}
      </CSSTransition>
    </div>
  );
};

export default Collapse;
